import { instance } from '@/api/instances'
import { addMixedDataLog } from '@/components/pillars/utils/addLog'
import { NOT_ATTACHED_MESSAGE } from '@/models/upload'
import useCertificateLog from '@/modules/user/hooks/useCertificateLog'
import { EventModel } from '@/wrappers/logger'

const fetchCurrentHealthPlan = async (store, { personId, signal }) => {
  try {
    store.commit('setCurrentHealthPlan', {})

    const currentHealthPlan = await instance.get(
      `persons/${personId}/health_plan`,
      { signal }
    )

    if (store.rootState.pillars.personId === personId) {
      store.commit('setCurrentHealthPlan', { ...currentHealthPlan })
    } else {
      addMixedDataLog('fetchCurrentHealthPlan')
      store.dispatch(
        'errors/setCriticalError',
        {
          type: 'mixed_data',
          context: 'health-plan'
        },
        { root: true }
      )
    }
  } catch (error) {
    store.commit('setCurrentHealthPlan', undefined)
    store.dispatch(
      'errors/setError',
      {
        context: 'health-plan',
        error,
        request: 'fetchCurrentHealthPlan'
      },
      { root: true }
    )
  }
}

const saveCurrentHealthPlan = async (store, currentHealthPlan) => {
  try {
    const updatedHealthPlan = await instance.put(
      `persons/${currentHealthPlan.personId}/health_plan`,
      {
        json: currentHealthPlan
      }
    )

    store.commit('saveCurrentHealthPlan', updatedHealthPlan)
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'pillars/healthPlan/saveCurrentHealthPlan' },
      { root: true }
    )
  }
}

const publishTasks = async (store, { taskType, demandId }) => {
  const { addLogOnPublishTask } = useCertificateLog()
  addLogOnPublishTask(
    store.rootState.pillars.personId,
    EventModel.HEALTH_PLAN,
    taskType,
    null,
    store.rootState.user?.certification?.token
  )

  try {
    await instance.post(
      `persons/${store.rootState.pillars.personId}/health_plan_publish`,
      {
        json: {
          type: taskType,
          caseId: demandId
        }
      },
      {
        headers: {
          'bird-id-token': store.rootState.user?.certification?.token
        }
      }
    )
    return true
  } catch (error) {
    store.dispatch(
      'errors/setError',
      {
        error,
        message: 'Ocorreu um erro ao publicar as tarefas, tente novamente.',
        request: 'pillars/healthPlan/publishTasks'
      },
      { root: true }
    )
    return false
  }
}

const uploadFile = async (store, { file, personId }) => {
  try {
    const result = await store.dispatch(
      'file/uploadFile',
      {
        file,
        personId
      },
      { root: true }
    )
    return result
  } catch (error) {
    store.dispatch(
      'errors/setError',
      {
        error,
        request: 'pillars/healthPlan/uploadFile',
        message: NOT_ATTACHED_MESSAGE
      },
      { root: true }
    )
  }
}

const searchMedicines = async (store, { query, signal }) => {
  try {
    const medicines = await instance.get('medicines', {
      ...signal,
      params: { query }
    })

    return medicines
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'pillars/healthPlan/searchMedicines' },
      { root: true }
    )
    return []
  }
}

const searchSentences = async (store, { query, signal }) => {
  try {
    const sentences = await instance.get('medicines/prescriptions/sentences', {
      ...signal,
      params: { query }
    })

    return sentences
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'pillars/healthPlan/searchSentences' },
      { root: true }
    )
    return []
  }
}

const searchTestsWithPackages = async (store, { query, signal, packages }) => {
  try {
    const tests = await instance.get('search_test_codes_with_packages', {
      ...signal,
      params: { query, packages: packages.toString() }
    })

    return tests
  } catch (error) {
    store.dispatch(
      'errors/setError',
      { error, request: 'pillars/healthPlan/searchTestsWithPackages' },
      { root: true }
    )
    return []
  }
}

const clearHealthPlan = store => {
  store.commit('setCurrentHealthPlan', {})
  store.commit('tasks/clearFilters')
  store.commit('tasks/setTaskCounters', {})
  store.commit('setSelectedTaskFilter', null)
}

/**
 * Select task filtered to show on PDA
 * @param {*} store
 * @param {*} selectedTaskFilter
 */
const setSelectedTaskFilter = (store, selectedTaskFilter) => {
  store.commit('setSelectedTaskFilter', selectedTaskFilter)
}

export default {
  publishTasks,
  fetchCurrentHealthPlan,
  saveCurrentHealthPlan,
  uploadFile,
  searchMedicines,
  searchSentences,
  searchTestsWithPackages,
  clearHealthPlan,
  setSelectedTaskFilter
}
